import { buildDensityConfig } from '../core/dot-engine/build-config';
import { DEFAULT_PARAMS } from '../presets/defaults';
import { BRAND_PALETTE_NAMES, isCanvasPresetConsistent, OUTCOME_HUES } from './catalog';
import type {
  GenerativeControls,
  ImageFrameControls,
  MosaicControls,
  PatternConfigurationV1,
  PatternModeConfiguration,
  StudioFailure,
} from './types';

const GENERATIVE_VARIANTS = [
  'breakthrough',
  'horizon',
  'ridge',
  'marketLife',
  'halfLife',
  'crowd',
  'current',
  'eddy',
  'confluence',
  'cascade',
  'braid',
  'ripple',
  'resonance',
  'beat',
  'standing',
  'moire',
  'edges',
  'territory',
  'crystal',
  'stipple',
  'converge',
  'radiate',
  'pinwheel',
  'arrows',
] as const;

const CANVAS_PRESET_IDS = [
  'morfi-cover',
  'x-header',
  'og-share',
  'square',
  'ratio-16-9',
  'ratio-3-2',
  'ratio-4-3',
  'ratio-4-5',
  'ratio-9-16',
  'custom',
] as const;

const GENERATIVE_CONTROL_KEYS = [
  'spacing',
  'minDot',
  'maxDot',
  'intensity',
  'gridAngle',
  'threshold',
  'definition',
  'baseFill',
  'strokeWeight',
  'gridType',
  'sourceLayout',
  'sourceCount',
  'sourceSize',
  'sourceElongation',
  'sourceFalloff',
  'sourceDivide',
  'folds',
  'rotation',
] as const;

const IMAGE_FRAME_CONTROL_KEYS = [
  'stripeWidth',
  'stripeGap',
  'verticalQuantization',
  'noise',
  'brightness',
  'contrast',
  'threshold',
  'invert',
] as const;

const MOSAIC_CONTROL_KEYS = [
  'cellSize',
  'brightness',
  'contrast',
  'threshold',
  'invert',
  'dotScale',
  'dither',
  'edgeMode',
  'edgeDepth',
  'edgeRoughness',
] as const;

const IMAGE_FRAME_DEFAULTS: ImageFrameControls = {
  stripeWidth: 2,
  stripeGap: 1,
  verticalQuantization: 4,
  noise: 0.08,
  brightness: 0,
  contrast: 1.2,
  threshold: 0.5,
  invert: false,
};

const MOSAIC_DEFAULTS: MosaicControls = {
  cellSize: 20,
  brightness: 0,
  contrast: 1.55,
  threshold: 1,
  invert: false,
  dotScale: 0.37,
  dither: false,
  edgeMode: 'off',
  edgeDepth: 5,
  edgeRoughness: 0.5,
};

type JsonObject = { [key: string]: JsonInput };
type JsonValue = JsonObject | JsonValue[] | boolean | number | string | null;
type JsonInput = JsonValue | undefined;

export type ConfigurationParseResult =
  | { readonly ok: true; readonly configuration: PatternConfigurationV1 }
  | { readonly ok: false; readonly failure: StudioFailure };

export function createDefaultGenerativeControls(): GenerativeControls {
  const density = buildDensityConfig('breakthrough', 'morfi-1337', 1500, 500);
  return {
    spacing: DEFAULT_PARAMS.spacing,
    minDot: DEFAULT_PARAMS.minDot,
    maxDot: DEFAULT_PARAMS.maxDot,
    intensity: DEFAULT_PARAMS.intensity,
    gridAngle: DEFAULT_PARAMS.gridAngle,
    threshold: DEFAULT_PARAMS.coverage,
    definition: DEFAULT_PARAMS.falloff,
    baseFill: DEFAULT_PARAMS.baseFill,
    strokeWeight: DEFAULT_PARAMS.strokeWeight,
    gridType: DEFAULT_PARAMS.gridType,
    sourceLayout: density.srcLayout,
    sourceCount: density.srcCount,
    sourceSize: density.srcSize,
    sourceElongation: density.srcElong,
    sourceFalloff: density.srcFalloff,
    sourceDivide: density.srcDivide,
    folds: density.kaleN,
    rotation: density.kaleRot,
  };
}

export function createDefaultImageFrameMode(): Extract<
  PatternModeConfiguration,
  { readonly kind: 'image-frame' }
> {
  return {
    kind: 'image-frame',
    mask: 'full',
    crop: { x: 0.5, y: 0.5 },
    controls: IMAGE_FRAME_DEFAULTS,
  };
}

export function createDefaultMosaicMode(): Extract<
  PatternModeConfiguration,
  { readonly kind: 'mosaic' }
> {
  return { kind: 'mosaic', crop: { x: 0.5, y: 0.5 }, controls: MOSAIC_DEFAULTS };
}

export function createDefaultPatternConfiguration(): PatternConfigurationV1 {
  return {
    schema: 'morfi.pattern-studio',
    version: 1,
    canvas: { preset: 'x-header', width: 1500, height: 500 },
    mode: {
      kind: 'generative',
      variant: 'breakthrough',
      controls: createDefaultGenerativeControls(),
    },
    seed: 'morfi-1337',
    palette: { kind: 'brand', name: 'brand' },
    outputColorMode: 'dark',
    background: { kind: 'transparent' },
  };
}

export function serializePatternConfiguration(configuration: PatternConfigurationV1): string {
  return `${JSON.stringify(canonicalConfiguration(configuration), null, 2)}\n`;
}

export function parsePatternConfiguration(input: string): ConfigurationParseResult {
  let candidate: JsonValue;
  try {
    candidate = JSON.parse(input) as JsonValue;
  } catch {
    // SILENT-CATCH: Parse errors become a typed result and are logged once by the apply boundary.
    return invalidConfiguration();
  }
  if (!isRecord(candidate)) return invalidConfiguration();
  if (candidate.schema !== 'morfi.pattern-studio') {
    return { ok: false, failure: { code: 'unsupported_configuration_reference' } };
  }
  if (candidate.version !== 1) {
    return { ok: false, failure: { code: 'unsupported_configuration_version' } };
  }
  if (
    !hasExactKeys(candidate, [
      'schema',
      'version',
      'canvas',
      'mode',
      'seed',
      'palette',
      'outputColorMode',
      'background',
    ]) ||
    !isCanvas(candidate.canvas) ||
    !isMode(candidate.mode) ||
    typeof candidate.seed !== 'string' ||
    candidate.seed.length < 1 ||
    candidate.seed.length > 128 ||
    !isPalette(candidate.palette) ||
    (candidate.outputColorMode !== 'light' && candidate.outputColorMode !== 'dark') ||
    !isBackground(candidate.background)
  ) {
    return invalidConfiguration();
  }
  return {
    ok: true,
    configuration: {
      schema: 'morfi.pattern-studio',
      version: 1,
      canvas: candidate.canvas,
      mode: candidate.mode,
      seed: candidate.seed,
      palette: candidate.palette,
      outputColorMode: candidate.outputColorMode,
      background: candidate.background,
    },
  };
}

function isCanvas(value: JsonInput): value is JsonObject & PatternConfigurationV1['canvas'] {
  if (!isRecord(value) || !hasExactKeys(value, ['preset', 'width', 'height'])) return false;
  if (
    !includes(CANVAS_PRESET_IDS, value.preset) ||
    typeof value.width !== 'number' ||
    typeof value.height !== 'number'
  ) {
    return false;
  }
  return isCanvasPresetConsistent({
    preset: value.preset,
    width: value.width,
    height: value.height,
  });
}

function canonicalConfiguration(configuration: PatternConfigurationV1): PatternConfigurationV1 {
  return {
    schema: configuration.schema,
    version: configuration.version,
    canvas: {
      preset: configuration.canvas.preset,
      width: configuration.canvas.width,
      height: configuration.canvas.height,
    },
    mode: canonicalMode(configuration.mode),
    seed: configuration.seed,
    palette:
      configuration.palette.kind === 'brand'
        ? { kind: 'brand', name: configuration.palette.name }
        : { kind: 'outcome', hue: configuration.palette.hue },
    outputColorMode: configuration.outputColorMode,
    background:
      configuration.background.kind === 'transparent'
        ? { kind: 'transparent' }
        : { kind: 'solid', color: configuration.background.color },
  };
}

function canonicalMode(mode: PatternModeConfiguration): PatternModeConfiguration {
  if (mode.kind === 'generative') {
    const controls = mode.controls;
    return {
      kind: 'generative',
      variant: mode.variant,
      controls: {
        spacing: controls.spacing,
        minDot: controls.minDot,
        maxDot: controls.maxDot,
        intensity: controls.intensity,
        gridAngle: controls.gridAngle,
        threshold: controls.threshold,
        definition: controls.definition,
        baseFill: controls.baseFill,
        strokeWeight: controls.strokeWeight,
        gridType: controls.gridType,
        sourceLayout: controls.sourceLayout,
        sourceCount: controls.sourceCount,
        sourceSize: controls.sourceSize,
        sourceElongation: controls.sourceElongation,
        sourceFalloff: controls.sourceFalloff,
        sourceDivide: controls.sourceDivide,
        folds: controls.folds,
        rotation: controls.rotation,
      },
    };
  }
  if (mode.kind === 'image-frame') {
    const controls = mode.controls;
    return {
      kind: 'image-frame',
      mask: mode.mask,
      crop: { x: mode.crop.x, y: mode.crop.y },
      controls: {
        stripeWidth: controls.stripeWidth,
        stripeGap: controls.stripeGap,
        verticalQuantization: controls.verticalQuantization,
        noise: controls.noise,
        brightness: controls.brightness,
        contrast: controls.contrast,
        threshold: controls.threshold,
        invert: controls.invert,
      },
    };
  }
  const controls = mode.controls;
  return {
    kind: 'mosaic',
    crop: { x: mode.crop.x, y: mode.crop.y },
    controls: {
      cellSize: controls.cellSize,
      brightness: controls.brightness,
      contrast: controls.contrast,
      threshold: controls.threshold,
      invert: controls.invert,
      dotScale: controls.dotScale,
      dither: controls.dither,
      edgeMode: controls.edgeMode,
      edgeDepth: controls.edgeDepth,
      edgeRoughness: controls.edgeRoughness,
    },
  };
}

function isMode(value: JsonInput): value is JsonObject & PatternModeConfiguration {
  if (!isRecord(value)) return false;
  if (value.kind === 'generative') {
    return (
      hasExactKeys(value, ['kind', 'variant', 'controls']) &&
      includes(GENERATIVE_VARIANTS, value.variant) &&
      isGenerativeControls(value.controls)
    );
  }
  if (value.kind === 'image-frame') {
    return (
      hasExactKeys(value, ['kind', 'mask', 'crop', 'controls']) &&
      includes(['full', 'circle', 'arch', 'diagonal', 'band', 'scatter'] as const, value.mask) &&
      isCrop(value.crop) &&
      isImageFrameControls(value.controls)
    );
  }
  return (
    value.kind === 'mosaic' &&
    hasExactKeys(value, ['kind', 'crop', 'controls']) &&
    isCrop(value.crop) &&
    isMosaicControls(value.controls)
  );
}

function isGenerativeControls(value: JsonInput): boolean {
  if (!isRecord(value) || !hasExactKeys(value, GENERATIVE_CONTROL_KEYS)) return false;
  return (
    inRange(value.spacing, 6, 48) &&
    inRange(value.minDot, 0, 100) &&
    inRange(value.maxDot, 0, 100) &&
    Number(value.minDot) <= Number(value.maxDot) &&
    inRange(value.intensity, 0, 1) &&
    inRange(value.gridAngle, 0, 90) &&
    inRange(value.threshold, 0, 0.5) &&
    inRange(value.definition, 0.5, 3) &&
    inRange(value.baseFill, 0, 1) &&
    inRange(value.strokeWeight, 0.5, 3) &&
    includes(['square', 'radial'] as const, value.gridType) &&
    includes(
      ['center', 'pair', 'row', 'corners', 'ring', 'scatter'] as const,
      value.sourceLayout,
    ) &&
    inRange(value.sourceCount, 1, 10, true) &&
    inRange(value.sourceSize, 0.3, 0.8) &&
    inRange(value.sourceElongation, 1, 3) &&
    inRange(value.sourceFalloff, 0.3, 0.7) &&
    inRange(value.sourceDivide, 0, 0.8) &&
    inRange(value.folds, 1, 12, true) &&
    inRange(value.rotation, 0, 360, false, true)
  );
}

function isImageFrameControls(value: JsonInput): boolean {
  if (!isRecord(value) || !hasExactKeys(value, IMAGE_FRAME_CONTROL_KEYS)) return false;
  return (
    inRange(value.stripeWidth, 1, 6, true) &&
    inRange(value.stripeGap, 0, 4, true) &&
    inRange(value.verticalQuantization, 1, 16, true) &&
    inRange(value.noise, 0, 0.4) &&
    inRange(value.brightness, -0.5, 0.5) &&
    inRange(value.contrast, 0.5, 2.5) &&
    inRange(value.threshold, 0, 1) &&
    typeof value.invert === 'boolean'
  );
}

function isMosaicControls(value: JsonInput): boolean {
  if (!isRecord(value) || !hasExactKeys(value, MOSAIC_CONTROL_KEYS)) return false;
  return (
    inRange(value.cellSize, 6, 48, true) &&
    inRange(value.brightness, -0.5, 0.5) &&
    inRange(value.contrast, 0.5, 2.5) &&
    inRange(value.threshold, 0, 1) &&
    typeof value.invert === 'boolean' &&
    inRange(value.dotScale, 0.3, 1) &&
    typeof value.dither === 'boolean' &&
    includes(['off', 'outer', 'content', 'both'] as const, value.edgeMode) &&
    inRange(value.edgeDepth, 2, 16, true) &&
    inRange(value.edgeRoughness, 0, 1)
  );
}

function isPalette(value: JsonInput): value is JsonObject & PatternConfigurationV1['palette'] {
  if (!isRecord(value)) return false;
  if (value.kind === 'brand') {
    return hasExactKeys(value, ['kind', 'name']) && includes(BRAND_PALETTE_NAMES, value.name);
  }
  return (
    value.kind === 'outcome' &&
    hasExactKeys(value, ['kind', 'hue']) &&
    includes(OUTCOME_HUES, value.hue)
  );
}

function isBackground(
  value: JsonInput,
): value is JsonObject & PatternConfigurationV1['background'] {
  if (!isRecord(value)) return false;
  if (value.kind === 'transparent') return hasExactKeys(value, ['kind']);
  return (
    value.kind === 'solid' &&
    hasExactKeys(value, ['kind', 'color']) &&
    typeof value.color === 'string' &&
    /^#[0-9a-fA-F]{6}$/.test(value.color)
  );
}

function isCrop(value: JsonInput): boolean {
  return (
    isRecord(value) &&
    hasExactKeys(value, ['x', 'y']) &&
    inRange(value.x, 0, 1) &&
    inRange(value.y, 0, 1)
  );
}

function isRecord(value: JsonInput): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasExactKeys(value: JsonObject, keys: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === keys.length && keys.every((key) => Object.hasOwn(value, key));
}

function includes<T extends string>(values: readonly T[], value: JsonInput): value is T {
  return typeof value === 'string' && (values as readonly string[]).includes(value);
}

function inRange(
  value: JsonInput,
  minimum: number,
  maximum: number,
  integer: boolean = false,
  maximumExclusive: boolean = false,
): boolean {
  if (typeof value !== 'number' || !Number.isFinite(value)) return false;
  if (integer && !Number.isInteger(value)) return false;
  return value >= minimum && (maximumExclusive ? value < maximum : value <= maximum);
}

function invalidConfiguration(): ConfigurationParseResult {
  return { ok: false, failure: { code: 'invalid_configuration' } };
}
